import { EmailConfig, Role } from '@goldcast/api/backend';

import { State, Speakers, Speaker, DefaultSpeakerRole } from './types';
import { transformServerSpeakers, getInitialState } from './utils';

const mutations = {
  setSpeakers(state: State, speakers: Speakers) {
    state.speakers = speakers;
  },
  setSpeakerRolesMap(state: State, speakerRoles: Role[]) {
    const speakerRolesMap = {};
    speakerRoles.forEach(role => {
      speakerRolesMap[role.name] = role;
    });
    state.speakerRolesMap = speakerRolesMap;
  },
  setIsSpeakerListLoading(state: State, isSpeakerListLoading: boolean) {
    state.isSpeakerListLoading = isSpeakerListLoading;
  },
  setSystemUserRolesMap(state: State, systemUserRoles: Role[]) {
    const systemUserRolesMap = {};
    systemUserRoles.forEach(role => {
      systemUserRolesMap[role.name] = role;
    });
    state.systemUserRolesMap = systemUserRolesMap;
  },
  setIsSpeakerEmailConfigLoading(state: State, isSpeakerEmailConfigLoading: boolean) {
    state.isSpeakerEmailConfigLoading = isSpeakerEmailConfigLoading;
  },
  setSpeakerInviteEmailConfig(state: State, speakerInviteEmailConfig: EmailConfig) {
    state.speakerInviteEmailConfig = speakerInviteEmailConfig;
  },
  resetSpeakerState(state: State) {
    Object.assign(state, getInitialState());
  },
  resetSelectedSpeakers(state: State) {
    state.selectedSpeakers = {};
    state.excludedSpeakers = {};
    state.areAllSpeakersSelected = false;
  },
  reorderSpeakers(state: State, speakers: Speaker[]) {
    const reorderRank: Speaker[] = speakers.map((speaker, index) => {
      return {
        ...speaker,
        rank: index
      };
    });
    const transformedSpeakers = transformServerSpeakers(reorderRank);
    state.speakers = transformedSpeakers;
  },
  deleteSpeaker(state: State, speakerId: string) {
    const speakers = { ...state.speakers };
    delete speakers[speakerId];
    state.speakers = speakers;
  },
  updateSpeaker(state: State, { speakerId, speaker }: { speakerId: string; speaker: Speaker }) {
    state.speakers = {
      ...state.speakers,
      [speakerId]: {
        ...state.speakers[speakerId],
        ...speaker,
        organization_user: {
          ...state.speakers[speakerId].organization_user,
          ...speaker.organization_user
        }
      }
    };
  },
  setToggleSelectSpeaker(state: State, { speaker }: { speaker: Speaker }) {
    if (!speaker) {
      return;
    }

    if (state.areAllSpeakersSelected) {
      const isSpeakerAlreadyExcluded = !!state.excludedSpeakers[speaker.id];
      if (isSpeakerAlreadyExcluded) {
        delete state.excludedSpeakers[speaker.id];
      } else {
        state.excludedSpeakers[speaker.id] = speaker;
      }
    } else {
      const isSpeakerAlreadySelected = !!state.selectedSpeakers[speaker.id];
      if (isSpeakerAlreadySelected) {
        delete state.selectedSpeakers[speaker.id];
      } else {
        state.selectedSpeakers[speaker.id] = speaker;
      }
    }
  },
  setToggleSelectAllSpeakers(state: State, selected?: boolean) {
    if (selected !== undefined) {
      state.areAllSpeakersSelected = selected;
    } else {
      state.areAllSpeakersSelected = !state.areAllSpeakersSelected;
    }

    state.selectedSpeakers = {};
    state.excludedSpeakers = {};
  },
  setTotalSpeakerCount(state: State, totalSpeakerCount: number) {
    state.totalSpeakerCount = totalSpeakerCount;
  },
  setIsFetchingMagicLink(
    state: State,
    { speakerId, isFetchingMagicLink }: { speakerId: string; isFetchingMagicLink: boolean }
  ) {
    state.speakers = {
      ...state.speakers,
      [speakerId]: {
        ...state.speakers[speakerId],
        isFetchingMagicLink
      }
    };
  },
  setDefaultSpeakerRole(state: State, defaultSpeakerRole: DefaultSpeakerRole) {
    state.defaultSpeakerRole = defaultSpeakerRole;
  },
  partialUpdateDefaultSpeakerRole(
    state: State,
    { id, payload }: { id: string; payload: Partial<DefaultSpeakerRole['role']> }
  ) {
    state.defaultSpeakerRole = {
      id,
      role: {
        ...state.defaultSpeakerRole?.role,
        ...payload
      } as DefaultSpeakerRole['role']
    };
  }
};

export default mutations;
